import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Check, ExternalLink, FileText, Link2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogementItems } from "@/components/LogementItems";
import { Panel } from "@/components/KpiCard";
import { MatchBadge } from "@/components/Badges";
import { EcartNote } from "@/components/EcartNote";
import { api, cleanParams, errMsg, money, moisLabel } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 px-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const BoutonFacture = ({ numero, testId }) => (
  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/factures-booking/pdf/${numero}`} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline" data-testid={testId}>
    <ExternalLink className="h-3 w-3" />Voir la facture
  </a>
);

const Apercu = ({ paiement, g }) => {
  if (!g) return <p className="text-xs text-amber-700" data-testid="facture-apercu">Aucune réservation Booking importée pour ce logement et ce mois : le paiement sera associé sans contrôle de la commission.</p>;
  const att = [1, 1.05, 1.14975].map((f) => Math.round(g.commission * f * 100) / 100);
  const best = att.reduce((a, b) => (Math.abs(paiement.debit - b) < Math.abs(paiement.debit - a) ? b : a));
  return <p className="text-xs text-slate-600" data-testid="facture-apercu">{g.nb} rés. · commissions {money(g.commission)} · attendu {money(best)} · écart {money(paiement.debit - best)}</p>;
};

const AssocierDialog = ({ paiement, groupes, logements, onClose, onDone }) => {
  const [logement, setLogement] = useState("");
  const [mois, setMois] = useState("");
  const [loading, setLoading] = useState(false);
  const fermer = () => { setLogement(""); setMois(""); onClose(); };
  const submit = async () => {
    setLoading(true);
    try { await api.post("/factures-booking", { bank_tx_id: paiement.id, logement, mois }); toast.success("Facture rapprochée"); setLogement(""); setMois(""); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const g = groupes.find((x) => x.logement === logement && x.mois === mois);
  return (
    <Dialog open={!!paiement} onOpenChange={(o) => !o && fermer()}>
      <DialogContent data-testid="facture-associer-dialog">
        <DialogHeader>
          <DialogTitle>Associer le paiement de {paiement && money(paiement.debit)}</DialogTitle>
          <DialogDescription>Choisissez le logement et le mois facturés (indiqués sur la facture Booking, « Période »).</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={logement} onValueChange={setLogement}>
            <SelectTrigger data-testid="facture-logement-select"><SelectValue placeholder="Logement" /></SelectTrigger>
            <SelectContent><LogementItems logements={logements} testPrefix="facture-logement" /></SelectContent>
          </Select>
          <Select value={mois} onValueChange={setMois}>
            <SelectTrigger data-testid="facture-mois-select"><SelectValue placeholder="Mois facturé" /></SelectTrigger>
            <SelectContent>{(paiement?.mois_possibles || []).map((m) => <SelectItem key={m} value={m} data-testid={`facture-mois-${m}`}>{moisLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {logement && mois && <Apercu paiement={paiement} g={g} />}
        <DialogFooter>
          <Button variant="outline" onClick={fermer} data-testid="facture-associer-cancel">Annuler</Button>
          <Button disabled={!logement || !mois || loading} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800" data-testid="facture-associer-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Associer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FacturesPdf = ({ liste }) => {
  const libres = liste.filter((f) => !f.payee);
  if (!libres.length) return null;
  return (
    <div className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="factures-pdf-libres">
      <p className="font-semibold flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Factures PDF importées sans paiement trouvé ({libres.length})</p>
      {libres.map((f) => <p key={f.numero} className="flex flex-wrap items-center gap-2" data-testid={`facture-pdf-${f.numero}`}>N° {f.numero} · {f.logement} · {moisLabel(f.mois)} · {money(f.total)} · du {f.date} <BoutonFacture numero={f.numero} testId={`facture-pdf-voir-${f.numero}`} /></p>)}
    </div>
  );
};

const PaiementRow = ({ p, act, onAssocier, reload }) => {
  const f = p.facture;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/70 align-top" data-testid={`facture-row-${p.id}`}>
      <td className="py-3 px-3 tabular text-xs whitespace-nowrap">{p.date}</td>
      <td className="py-3 px-3 text-xs">{p.description}</td>
      <td className="py-3 px-3 text-right tabular font-semibold text-red-600">{money(p.debit)}</td>
      <td className="py-3 px-3 text-xs" data-testid={`facture-cible-${p.id}`}>
        {f ? <><p className="font-medium text-sm">{f.logement} · {moisLabel(f.mois)}</p>
          <p className="text-slate-500">{f.facture_pdf && !f.reservation_ids.length ? "facture PDF · relevé Booking non importé" : `${f.reservation_ids.length} rés.`} · commissions {money(f.commission)} · {f.calcul} = {money(f.montant_attendu)}{f.factures_no.length ? ` · facture ${f.factures_no.join(", ")}` : ""}</p>
          {f.facture_pdf && <BoutonFacture numero={f.facture_pdf} testId={`facture-voir-${p.id}`} />}</>
          : <span className="text-slate-400">Aucune facture ni commission correspondante ({moisLabel(p.mois_possibles[2])} – {moisLabel(p.mois_possibles[0])})</span>}
      </td>
      <td className={`py-3 px-3 text-right tabular text-xs ${f && Math.abs(f.ecart) > 0.009 ? "text-amber-700" : "text-slate-500"}`}>{f ? money(f.ecart) : "—"}</td>
      <td className="py-3 px-3"><EcartNote txId={p.id} explication={p.explication} note={p.note} onSaved={reload} /></td>
      <td className="py-3 px-3"><MatchBadge match={f} testId={`facture-status-${p.id}`} /></td>
      <td className="py-3 px-3 text-right">
        {!f ? <Button size="sm" variant="outline" className="h-8" onClick={() => onAssocier(p)} data-testid={`facture-associer-${p.id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Associer</Button> : (
          <div className="flex justify-end gap-1">
            {f.statut === "propose" && <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700 hover:bg-emerald-50" onClick={() => act("post", `/factures-booking/${f.id}/valider`, "Facture validée")} data-testid={`facture-valider-${p.id}`}><Check className="h-4 w-4" /></Button>}
            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => act("delete", `/factures-booking/${f.id}`, "Rapprochement retiré")} data-testid={`facture-rejeter-${p.id}`}><X className="h-4 w-4" /></Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const FacturesBookingPanel = ({ periode, refreshKey }) => {
  const [data, setData] = useState(null);
  const [assoc, setAssoc] = useState(null);
  const load = useCallback(() => {
    api.get("/factures-booking", { params: cleanParams(periode || {}) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.debut, periode?.fin]);
  useEffect(load, [load, refreshKey]);
  const act = async (method, url, msg) => {
    try { await api[method](url); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  return (
    <Panel title={`Factures Booking payées (${data.stats.rapproches}/${data.stats.paiements} rapprochées · ${money(data.stats.total)})`} className="mt-6" testId="factures-booking-panel">
      <p className="text-xs text-slate-500 mb-3">Chaque paiement de facture Booking.com est rapproché de la facture PDF importée (même montant), sinon des commissions d'un des 3 mois précédents, logement par logement (avec ou sans TPS/TVQ sur la commission).</p>
      <div className="overflow-x-auto -mx-5">
        <SmartTable id="factures-booking" className="w-full text-sm min-w-[1150px]" data-testid="factures-booking-table">
          <thead><tr><Th>Date</Th><Th>Description</Th><Th right>Payé</Th><Th>Commissions facturées</Th><Th right>Écart</Th><Th>Explication de l'écart</Th><Th>Statut</Th><Th right>Actions</Th></tr></thead>
          <tbody>
            {data.paiements.map((p) => <PaiementRow key={p.id} p={p} act={act} onAssocier={setAssoc} reload={load} />)}
            {!data.paiements.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Aucun paiement de facture Booking dans le relevé bancaire.</td></tr>}
          </tbody>
        </SmartTable>
      </div>
      <FacturesPdf liste={data.factures_pdf || []} />
      <AssocierDialog paiement={assoc} groupes={data.groupes_libres} logements={data.logements || []} onClose={() => setAssoc(null)} onDone={() => { setAssoc(null); load(); }} />
    </Panel>
  );
};
